import { supabase } from "./supabase";
import type { TransactionWithLignes } from "./transactions";

export interface ExportDateRange {
  dateDebut: string;
  dateFin: string;
}

/** Récupère les transactions affectées d'un dossier pour export (optionnel : plage de dates) */
export async function fetchTransactionsPourExport(
  dossierId: string | null,
  range?: ExportDateRange
): Promise<TransactionWithLignes[]> {
  let q = supabase
    .from("transactions")
    .select("*, transaction_lignes(*)")
    .eq("statut", "affectee")
    .order("date", { ascending: true });

  if (dossierId) {
    q = q.eq("dossier_id", dossierId);
  } else {
    q = q.is("dossier_id", null);
  }

  if (range?.dateDebut) {
    q = q.gte("date", range.dateDebut);
  }
  if (range?.dateFin) {
    q = q.lte("date", range.dateFin);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((t) => ({
    ...t,
    transaction_lignes: t.transaction_lignes || [],
  }));
}

export type ExportDateFormat = "iso" | "sage";

/** Options d'export CSV pour compatibilité logiciels comptables */
export interface ExportCsvOptions {
  /** Format de date : iso (AAAA-MM-JJ) ou sage (JJ/MM/AAAA) */
  dateFormat?: ExportDateFormat;
  sep?: string;
}

/** Convertit une date ISO (AAAA-MM-JJ) vers le format demandé */
function formatDateExport(isoDate: string, format: ExportDateFormat): string {
  if (!isoDate || isoDate.length < 10) return isoDate;
  if (format === "sage") {
    const [y, m, d] = isoDate.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  return isoDate.slice(0, 10);
}

/** Journal : BQ pour toutes les opérations (banque, mobile money, saisie manuelle) */
function getJournal(_operateur: string): string {
  return "BQ";
}

/** Compte trésorerie selon l'opérateur (OHADA) */
function getCompteTresorerie(operateur: string): { numero: string; libelle: string } {
  switch (operateur) {
    case "banque":
      return { numero: "521", libelle: "Banques locales" };
    case "orange_money":
    case "mtn":
    case "wave":
      return { numero: "538", libelle: "Autres organismes financiers" };
    default:
      return { numero: "521", libelle: "Banques locales" };
  }
}

interface LigneExport {
  compte: string;
  libelleCompte: string;
  libelle: string;
  debit: number;
  credit: number;
}

/** Construit les lignes d'écriture complètes (y compris TVA et trésorerie si manquantes) */
function buildLignesEcriture(tx: TransactionWithLignes): LigneExport[] {
  const lignes = (tx.transaction_lignes || []).sort((a, b) => a.ordre - b.ordre);
  const result: LigneExport[] = [];
  let totalTVA = 0;
  let hasTvaLigne = false;
  let hasTresorerieLigne = false;
  const libelleTx = tx.libelle || "";

  for (const l of lignes) {
    const compte = String(l.compte_numero || "").trim();
    const classe = compte.charAt(0);
    const mtHt = Math.abs(Number(l.montant_ht) || 0);
    const mtTva = Math.abs(Number(l.montant_tva) || 0);

    if (compte.startsWith("44")) {
      hasTvaLigne = true;
    }
    if (classe === "5") {
      hasTresorerieLigne = true;
    }

    let montant = 0;
    let sens: "debit" | "credit" = "debit";

    if (classe === "6") {
      montant = mtHt;
      sens = "debit";
      totalTVA += mtTva;
    } else if (classe === "7") {
      montant = mtHt;
      sens = "credit";
      totalTVA += mtTva;
    } else if (compte.startsWith("44")) {
      montant = mtTva;
      if (tx.type === "entree") sens = "credit";
      else sens = "debit";
    } else if (classe === "5") {
      montant = mtHt + mtTva;
      sens = tx.type === "entree" ? "debit" : "credit";
    } else if (classe === "4") {
      montant = mtHt + mtTva;
      if (compte.startsWith("41")) sens = tx.type === "entree" ? "credit" : "debit";
      else if (compte.startsWith("40")) sens = tx.type === "sortie" ? "debit" : "credit";
      else sens = tx.type === "entree" ? "debit" : "credit";
    } else {
      montant = mtHt + mtTva;
      sens = tx.type === "entree" ? "debit" : "credit";
    }

    if (montant > 0) {
      result.push({
        compte,
        libelleCompte: l.compte_libelle || compte,
        libelle: l.libelle || libelleTx,
        debit: sens === "debit" ? montant : 0,
        credit: sens === "credit" ? montant : 0,
      });
    }
  }

  if (totalTVA > 0 && !hasTvaLigne) {
    const compteTva = tx.type === "entree" ? "443" : "445";
    const libelleTva = tx.type === "entree" ? "État, T.V.A. facturée" : "État, T.V.A. récupérable";
    result.push({
      compte: compteTva,
      libelleCompte: libelleTva,
      libelle: libelleTx,
      debit: tx.type === "sortie" ? totalTVA : 0,
      credit: tx.type === "entree" ? totalTVA : 0,
    });
  }

  if (!hasTresorerieLigne) {
    const { numero, libelle } = getCompteTresorerie(tx.operateur);
    const ttc = Math.abs(Number(tx.montant_ttc) || 0);
    if (ttc > 0) {
      result.push({
        compte: numero,
        libelleCompte: libelle,
        libelle: libelleTx,
        debit: tx.type === "entree" ? ttc : 0,
        credit: tx.type === "sortie" ? ttc : 0,
      });
    }
  }

  return result;
}

/** Génère un CSV des écritures pour import ERP (format générique, compatible Sage, Banana, etc.) */
export function buildExportCsv(
  transactions: TransactionWithLignes[],
  options: ExportCsvOptions = {}
): string {
  const { dateFormat = "iso", sep = ";" } = options;
  const header = [
    "N° pièce",
    "Date",
    "Journal",
    "Compte",
    "Libellé compte",
    "Libellé",
    "Débit",
    "Crédit",
    "Devise",
  ].join(sep);

  const rows: string[] = [header];

  let numPiece = 1;
  for (const tx of transactions) {
    const dateFormatted = formatDateExport(tx.date, dateFormat);
    const journal = getJournal(tx.operateur);

    const lignesExport = buildLignesEcriture(tx);
    if (lignesExport.length === 0) continue;

    for (const l of lignesExport) {
      const libelleCompte = (l.libelleCompte || "").replace(/"/g, '""');
      const libelle = (l.libelle || "").replace(/"/g, '""');
      rows.push(
        [
          numPiece,
          dateFormatted,
          journal,
          l.compte,
          `"${libelleCompte}"`,
          `"${libelle}"`,
          l.debit > 0 ? String(Math.round(Number(l.debit))) : "",
          l.credit > 0 ? String(Math.round(Number(l.credit))) : "",
          "XOF",
        ].join(sep)
      );
    }
    numPiece += 1;
  }

  return "\uFEFF" + rows.join("\n"); // BOM UTF-8 pour Excel
}

/** Télécharge un blob en fichier */
export function downloadBlob(blob: Blob, filename: string): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
